<template>
	<Modal
		v-model:visible="visible"
		:title="$t('settings.profileManagement.geoFencingModal.header')"
		@ok="onOk"
		@cancel="onCancel"
	>
		<Form
			:model="formState"
			:label-col="{ span: 12 }"
			:wrapper-col="{ span: 12 }"
			:colon="false"
		>
			<FormItem
				:label="$t('settings.profileManagement.geoFencingModal.enabled')"
				v-bind="validateInfos.enabled"
			>
				<LabeledSwitch v-model="formState.enabled" />
			</FormItem>
			<FormItem
				:label="$t('settings.profileManagement.geoFencingModal.bCoordLabel')"
				name="bCoord"
				v-bind="validateInfos.bCoord"
			>
				<InputDecimal
					v-model="formState.bCoord"
					:allowPoint="true"
					range="latitude"
					@keyup.enter="onOk"
				/>
			</FormItem>
			<FormItem
				:label="$t('settings.profileManagement.geoFencingModal.lCoordLabel')"
				name="lCoord"
				v-bind="validateInfos.lCoord"
			>
				<InputDecimal
					v-model="formState.lCoord"
					:allowPoint="true"
					@keyup.enter="onOk"
				/>
			</FormItem>
			<FormItem
				:label="$t('settings.profileManagement.geoFencingModal.radiusLabel')"
				name="radius"
				v-bind="validateInfos.radius"
			>
				<InputDecimal
					v-model="formState.radius"
					:allowPoint="true"
					range="non-negative"
					@keyup.enter="onOk"
				/>
			</FormItem>
		</Form>
		<template #footer>
			<Button key="back" @click="onCancel">{{ $t('settings.profileManagement.geoFencingModal.cancelButtonLabel') }}</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" @click="onOk">{{ $t('settings.profileManagement.geoFencingModal.okButtonLabel') }}</Button>
		</template>
	</Modal>
</template>

<script>
import { reactive, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';

import { useI18n } from 'vue-i18n';
import camelCase from 'lodash/camelCase';
import { Modal, Form, FormItem, Button, message } from 'ant-design-vue';

import Validators from '@/api/Validators';

import { useProfileUpdate } from '@/components/settings/profiles/useProfileUpdate';
import { runValidatedSubmit } from '@/components/common/formModal';

import LabeledSwitch from '@/components/common/LabeledSwitch';
import InputDecimal from '@/components/common/InputDecimal';

export default {
	components: {
		Modal, Form, FormItem, Button,
		LabeledSwitch,
		InputDecimal,
	},
	props: {
		modelValue: Boolean,
		profile: Object,
		onProfileUpdate: Function,
	},
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const visible = useVModel(props, 'modelValue', emit);
		const formState = reactive({
			enabled: false,
			bCoord: '',
			lCoord: '',
			radius: '',
		});

		const {
			resetFields,
			validate,
			validateInfos,
		} = Validators.useGeoFencingFormRules(formState);

		const isSubmitting = ref(false);

		watch(visible, (value) => {
			if (value) {
				resetFields();
				formState.enabled = props.profile.geoFencingEnabled;
				formState.bCoord = props.profile.geoFencingBCoord || '';
				formState.lCoord = props.profile.geoFencingLCoord || '';
				formState.radius = props.profile.geoFencingRadius || '';
				isSubmitting.value = false;
			}
		});

		const { profileUpdate } = useProfileUpdate(
			() => { },
			() => {
				isSubmitting.value = false;
				message.success(t('settings.profileManagement.geoFencingModal.messages.success'));
				visible.value = false;
				props.onProfileUpdate();
			},
			response => {
				['geo_fencing_b_coord', 'geo_fencing_l_coord', 'geo_fencing_radius'].forEach(element => {
					if (response.errors[element]) {
						let jsElement = camelCase(element.replace('geo_fencing_', ''));
						validateInfos[jsElement].validateStatus = 'error';
						validateInfos[jsElement].help = response.errors[element];
						delete response.errors[element];
					}
				});
				isSubmitting.value = false;
			}
		);

		const onOk = () => {
			runValidatedSubmit(validate, isSubmitting, () => {
				profileUpdate(props.profile, {
					geo_fencing_enabled: formState.enabled,
					geo_fencing_b_coord: formState.bCoord,
					geo_fencing_l_coord: formState.lCoord,
					geo_fencing_radius: formState.radius,
				});
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			visible,
			formState,
			validateInfos,
			isSubmitting,
			onOk,
			onCancel,
		};
	},
};
</script>
